'use client';

import { useTheme } from '@mui/material/styles';
import { useEffect } from 'react';
import Box from '@mui/material/Box';

interface BackgroundWrapperProps {
    /* Props for BackgroundWrapper component including children. */

    children: React.ReactNode;
}

// COLOUR TO RGBA
function colourToRgba(colour: string, alpha: number): string {
    /* Converts a hex colour to rgba so edge-fade gradients interpolate to the page colour rather than black. */

    if (!colour.startsWith('#')) {
        return colour;
    }

    const hex = colour.slice(1);
    const full = hex.length === 3 ? hex.split('').map((char) => char + char).join('') : hex;
    const value = parseInt(full, 16);
    const red = (value >> 16) & 255;
    const green = (value >> 8) & 255;
    const blue = value & 255;

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

// BACKGROUND LAYERS
function buildBackgroundLayers(
    backgroundImage: string,
    backgroundColor: string | undefined,
    backgroundRepeat: string,
    backgroundSize: string,
    backgroundEdgeFade: boolean,
    backgroundEdgeFadeSize: string,
): {
    backgroundImage: string;
    backgroundSize: string;
    backgroundPosition: string;
    backgroundRepeat: string;
} {
    /* Builds the CSS background layers for the viewport-fixed overlay, including optional edge-fade gradients. */

    if (!backgroundEdgeFade) {
        return {
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize,
            backgroundPosition: 'top center',
            backgroundRepeat,
        };
    }

    const fadeColour = backgroundColor || '#ffffff';
    const solid = colourToRgba(fadeColour, 1);
    const clear = colourToRgba(fadeColour, 0);
    const edge = backgroundEdgeFadeSize;

    return {
        backgroundImage: [
            `linear-gradient(to right, ${solid} 0%, ${clear} ${edge}, ${clear} calc(100% - ${edge}), ${solid} 100%)`,
            `linear-gradient(to bottom, ${solid} 0%, ${clear} ${edge}, ${clear} 100%)`,
            `url(${backgroundImage})`,
        ].join(', '),
        backgroundSize: `100% 100%, 100% 100%, ${backgroundSize}`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
    };
}

// BACKGROUND WRAPPER
export default function BackgroundWrapper({ children }: BackgroundWrapperProps) {
    /**
     * Applies the theme page colour to the document body and pins the
     * background image to the viewport so it does not grow or shift when
     * page content expands.
     */

    const theme = useTheme();
    const backgroundImage = theme.custom.backgroundImage;
    const backgroundColor = theme.custom.backgroundColor;
    const backgroundRepeat = theme.custom.backgroundRepeat ?? 'no-repeat';
    const backgroundSize = theme.custom.backgroundSize ?? 'auto 100%';
    const backgroundEdgeFade = theme.custom.backgroundEdgeFade ?? false;
    const backgroundEdgeFadeSize = theme.custom.backgroundEdgeFadeSize ?? '16%';

    useEffect(() => {
        // keep the page colour on the body so overscroll matches the theme
        const previousColor = document.body.style.backgroundColor;
        const previousCssBackground = document.documentElement.style.getPropertyValue('--background');

        if (backgroundColor) {
            document.body.style.backgroundColor = backgroundColor;
            document.documentElement.style.setProperty('--background', backgroundColor);
        }

        return () => {
            document.body.style.backgroundColor = previousColor;
            if (previousCssBackground) {
                document.documentElement.style.setProperty('--background', previousCssBackground);
            } else {
                document.documentElement.style.removeProperty('--background');
            }
        };
    }, [backgroundColor]);

    const overlayLayers = backgroundImage
        ? buildBackgroundLayers(
            backgroundImage,
            backgroundColor,
            backgroundRepeat,
            backgroundSize,
            backgroundEdgeFade,
            backgroundEdgeFadeSize,
        )
        : null;

    return (
        <>
            {overlayLayers && (
                <Box
                    aria-hidden
                    sx={{
                        // lock to the viewport; do not use inset 0 or the overlay will stretch with the document
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100vh',
                        '@supports (height: 100dvh)': {
                            height: '100dvh',
                        },
                        pointerEvents: 'none',
                        zIndex: 0,
                        backgroundColor: backgroundColor || undefined,
                        ...overlayLayers,
                    }}
                />
            )}
            <Box sx={{ position: 'relative', zIndex: 1 }}>
                {children}
            </Box>
        </>
    );
}
